// EnhancedSubmissionDetailsPage.tsx
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { TabNavigation } from "../components/ui/tab-navigation";
import { Badge } from "../components/ui/badge";
import {
  CustomTabs,
  CustomTabsList,
  CustomTabsTrigger,
  CustomTabsContent,
} from "../components/ui/custom-tabs";
import { Separator } from "../components/ui/separator";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  ArrowLeft,
  Download,
  FileText,
  Calendar,
  Clock,
  User,
  GraduationCap,
  Eye,
  Play,
  Code,
  BarChart3,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  Save,
  Edit3,
  Upload,
  GitBranch,
  Flag,
  Power,
  History,
  Check,
  BadgeCheck,
  CheckCheck,
  CheckCircle2,
  Rocket,
  Terminal,
  Laptop,
  Settings2,
} from "lucide-react";
import paths from "../routes";
import { useSubmissionNavigation } from "../contexts/SubmissionNavigationContext";
import { SubmissionNavigationControls } from "../components/SubmissionNavigationControls";
import { useSubmissionByIdQuery } from "../api/submissionApi"; // path based on your structure
import { formatDistanceToNow } from "../helper/date-fns";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import {
  useStopSandboxMutation,
  useLaunchReviewSandboxMutation,
  useWorkspaceStatusQuery,
  useCancelProvisioningMutation,
  getWorkspaceStatus,
  listSessions,
  getActiveWorkspaceSession,
} from "../api/jupyterManagementApi";
import { useSandboxLifecycle } from "../hooks/useSandboxLifecycle";
import type { SandboxStatus } from "../hooks/useSandboxLifecycle";
import { useJupyterWorkspace } from "../contexts/JupyterWorkspaceContext";
import {
  GradeResponse,
  GradeSubmissionRequest,
  saveRubrics,
} from "../api/createGradeApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import FeedbackDialog from "../components/FeedbackDialog";
import { useAuth } from "../App";
import axios from "axios";
import JSZip from "jszip";
import { sub } from "date-fns";
import SandboxTermsModal from "../components/SandboxTerms";
import { LocalRunButton } from "../components/LocalRunButton";
import { AgentStatusBadge } from "../components/AgentStatusBadge";
import { cn } from "../lib/utils";
import { useAgentSessionStatusQuery } from "../api/jupyterManagementApi";
import { SandboxPanel } from "../components/sandbox/SandboxPanel";

type NavSubmission = {
  id: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  assignmentId: string;
  assignmentTitle: string;
  attempts: number;
  checkpointTitle: string;
  submittedAt: string;
  status: "submitted" | "reviewed" | "completed" | "returned";
  grade?: any;
  course?: string;
  feedback?: string;
  comments?: string;
  filesSubmitted?: any;
  metrics?: { accuracy?: string; executionTime?: string };
  submission: any;
  group?: any;
};

type LocationState = {
  submissions?: NavSubmission[];
  currentSubmissionId?: string;
  assignmentTitle?: string;
};

// SandboxStatus is now imported from useSandboxLifecycle

export default function EnhancedSubmissionDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const state = (location.state ?? {}) as LocationState;

  const {
    submissions,
    currentSubmission,
    currentIndex,
    assignmentTitle,
    isLoading,
    setSubmissions,
    goToSubmission,
    refreshSubmissions,
    hasNext,
    hasPrevious,
    goToNext,
    goToPrevious,
  } = useSubmissionNavigation();

  useEffect(() => {
    if (state?.submissions && state?.assignmentTitle) {
      if (!submissions?.length) {
        setSubmissions(state.submissions, state.assignmentTitle);
      }
      if (state.currentSubmissionId) {
        const idx = (state.submissions || submissions).findIndex(
          (s) => s.id === state.currentSubmissionId
        );
        if (idx >= 0) goToSubmission(idx);
      }
    }
  }, [state, submissions, setSubmissions, goToSubmission]);

  const submissionId = currentSubmission?.id;
  const submissionGradeId = currentSubmission?.submissionId;
  const {
    data: detailResp,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useSubmissionByIdQuery(submissionId);
  const detailed = detailResp?.data as Partial<NavSubmission> | undefined;
  const merged = React.useMemo(() => {
    return {
      ...(currentSubmission ?? {}),
      ...(detailed ?? {}),
    } as NavSubmission;
  }, [currentSubmission, detailed]);
  const { workspace, updateWorkspace, resetWorkspace, isConnectionVerified } = useJupyterWorkspace();
  const { data: agentSessionStatus } = useAgentSessionStatusQuery(submissionId || "", workspace.globalSandboxMode === "local" && workspace.workspaceType === "local");
  const agentStatus = agentSessionStatus?.status || "SLEEPING";

  const launchReviewSandboxMutation = useLaunchReviewSandboxMutation();
  const stopSandboxMutation = useStopSandboxMutation();
  const cancelProvisioningMutation = useCancelProvisioningMutation();
  const [submissionPath, setSubmissionPath] = useState<string>("");
  const [activeTab, setActiveTab] = React.useState("overview");
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [isEditingFeedback, setIsEditingFeedback] = React.useState(false);
  const queryClient = useQueryClient();
  const [feedbackText, setFeedbackText] = React.useState<string>("");
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [provisioningStartedAt, setProvisioningStartedAt] = useState<number | null>(null);

  // ── Sandbox lifecycle (unified hook) ──────────────────────────────────────
  const sandboxLifecycle = useSandboxLifecycle({
    sandboxId: submissionId || "",
    launchFn: () => launchReviewSandboxMutation.mutateAsync(submissionId || ""),
    onActive: (data) => {
      setSubmissionPath(data.folderPath || "");
      updateWorkspace({
        project: { id: submissionId! },
        kernelId: data.kernel?.id,
        sessionId: data.session?.id,
        workspaceUrl: data.server?.url,
        status: "active",
        instanceId: data.server?.state?.object_name,
        createdAt: data.server?.started,
        lastActivity: data.kernel?.last_activity,
        folderPath: data.folderPath,
        notebookPath: data.notebookPath,
        serverReady: data.server?.ready || true,
        kernelExecutionState: data.kernel?.execution_state,
      });
    },
    onIdle: () => resetWorkspace(),
    onProvisioning: () => {
      if (workspace.status !== "provisioning") {
        updateWorkspace({ 
          status: "provisioning", 
          project: { id: submissionId! } 
        });
      }
    },
    sessionId: workspace.sessionId,
  });

  const {
    sandboxStatus,
    setSandboxStatus,
    handleLaunch,
    handleCancel,
    handleStop,
  } = sandboxLifecycle;

  // ── Sync workspace context → sandboxStatus on mount/context change ──────────
  useEffect(() => {
    if (sandboxStatus.status === "provisioning" || sandboxStatus.status === "stopping") return;

    // Safety guard: if we just started provisioning, don't let the context sync revert it
    const isRecentlyStarted = provisioningStartedAt && (Date.now() - provisioningStartedAt < 60000);
    if (isRecentlyStarted) return;

    if (workspace.project?.id === currentSubmission?.id && workspace.status !== "none") {
      setSandboxStatus({ status: workspace.status as any });
      setSubmissionPath(workspace.folderPath || "");
    } else if (workspace.project?.id === currentSubmission?.id && workspace.status === "none") {
      setSandboxStatus({ status: "none" });
    } else if (workspace.project?.id !== currentSubmission?.id) {
      setSandboxStatus({ status: "none" });
    }
  }, [workspace.project?.id, workspace.status, currentSubmission?.id, provisioningStartedAt, setSandboxStatus]);

  // Mount-time reconcile: Redis lease + workspaceStatus vs context (heartbeats are off this route).
  useEffect(() => {
    if (!submissionId || !isConnectionVerified) return;
    let cancelled = false;

    getActiveWorkspaceSession(submissionId).then(async (active) => {
      if (cancelled) return;
      try {
        const probe = await getWorkspaceStatus(submissionId);
        if (cancelled) return;

        const forThisEntity = workspace.project?.id === submissionId;
        const uiStaleSession =
          forThisEntity &&
          (workspace.status === "active" || workspace.status === "error");
        const panelThinksLive =
          sandboxStatus.status === "active" || sandboxStatus.status === "error";

        if (!active) {
          const qcPayload =
            probe.status === "provisioning"
              ? probe
              : probe.status === "error"
                ? probe
                : { status: "idle" as const, progress: probe?.progress ?? 0, message: probe?.message };
          queryClient.setQueryData(["jupyter", "workspace", "status", submissionId], qcPayload);

          if (uiStaleSession || panelThinksLive) {
            resetWorkspace();
            setSandboxStatus({ status: "none" });
          } else if (probe.status === "provisioning") {
            setSandboxStatus({ status: "provisioning", provisioningProgress: probe.progress || 10 });
            setProvisioningStartedAt(Date.now());
            updateWorkspace({
              status: "provisioning",
              project: { id: submissionId! },
            });
          }
          return;
        }

        if (active && (probe.status === "idle" || probe.status === "error")) {
          resetWorkspace();
          setSandboxStatus({ status: "none" });
          return;
        }

        if (probe.status === "provisioning") {
          setSandboxStatus({ status: "provisioning", provisioningProgress: probe.progress || 10 });
          setProvisioningStartedAt(Date.now());
          updateWorkspace({
            status: "provisioning",
            project: { id: submissionId! },
          });
          return;
        }

        if (probe.status !== "active" || !probe.data) {
          return;
        }

        const d = probe.data;
        let recoveredKernelId = d.kernel?.id || "";
        let recoveredNotebookPath = d.notebookPath;

        if (!recoveredKernelId) {
          try {
            const sessions = await listSessions();
            const match = sessions.find((s: any) => s.path?.includes(submissionId));
            if (match?.kernel?.id) {
              recoveredKernelId = match.kernel.id;
              if (!recoveredNotebookPath && match.path) recoveredNotebookPath = `/${match.path}`;
            }
          } catch (_) {}
        }

        setSubmissionPath(d.folderPath || "");
        updateWorkspace({
          project: { id: submissionId },
          kernelId: recoveredKernelId,
          sessionId: d.sessionId || null,
          workspaceUrl: d.server?.url || "",
          status: "active",
          folderPath: d.folderPath,
          notebookPath: recoveredNotebookPath,
          serverReady: d.server?.ready || false,
          kernelExecutionState: d.kernel?.execution_state || "idle",
          remainingTime: d?.remainingTime ?? null,
          totalAllowedTime: d?.totalAllowedTime ?? null,
          timeUsed: d?.timeUsed ?? null,
        });
        setSandboxStatus({ status: "active" });
      } catch (_) {}
    }).catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [
    submissionId,
    isConnectionVerified,
    location.pathname,
    workspace.project?.id,
    workspace.status,
    sandboxStatus.status,
    queryClient,
    updateWorkspace,
    setSandboxStatus,
    resetWorkspace,
  ]);


  const handleFeedbackSubmit = async (
    payloadOrBody: GradeResponse | GradeSubmissionRequest | any,
    opts?: { source?: string }
  ) => {
    try {
      if (opts?.source === "dialog") {
        const serverPayload =
          payloadOrBody?.data && payloadOrBody.data.data
            ? payloadOrBody.data
            : payloadOrBody;

        const gradeResp: GradeResponse = (serverPayload?.data ??
          serverPayload) as GradeResponse;


        // Use submissionId variable which is currentSubmission?.id
        if (submissionId) {

          // Invalidate and refetch queries to trigger immediate update
          await queryClient.invalidateQueries({
            queryKey: ["submissions", "detail", submissionId],
          });

          await queryClient.invalidateQueries({
            queryKey: ["progressDetails", submissionId],
          });
          
          // Invalidate submissions list
          await queryClient.invalidateQueries({
            queryKey: ["submissions"],
          });

          // Force immediate refetch of submission details
          await queryClient.refetchQueries({
            queryKey: ["submissions", "detail", submissionId],
            type: "active",
          });
        }

        // Toast already shown by FeedbackDialog
        return;
      }
      const response: GradeResponse = await saveRubrics(
        payloadOrBody as GradeSubmissionRequest
      );

      toast.success(response.message || "Feedback submitted successfully!");

      // Use submissionId variable which is currentSubmission?.id
      if (submissionId) {
        await queryClient.invalidateQueries({
          queryKey: ["progressDetails", submissionId],
        });
        // Invalidate submission details to refetch updated data
        await queryClient.invalidateQueries({
          queryKey: ["submissions", "detail", submissionId],
        });

        // Force immediate refetch of submission details
        await queryClient.refetchQueries({
          queryKey: ["submissions", "detail", submissionId],
          type: "active",
        });
      }

      // Force refetch all submissions list queries to refresh the table immediately
      await queryClient.refetchQueries({
        queryKey: ["submissions", "list"],
        type: "active",
      });

      await queryClient.invalidateQueries({
        queryKey: ["submissions"],
      });

      // Refresh the submissions list to update the table
      await refreshSubmissions();
    } catch (error: any) {
      console.error("Error saving feedback:", error);
      toast.error("Failed to submit feedback. Please try again.");
    }
  };

  useEffect(() => {
    if (merged?.feedback != null) setFeedbackText(merged.feedback);
  }, [merged?.feedback]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshSubmissions();
      toast.success("Submission data refreshed");
    } catch (e) {
      toast.error("Failed to refresh submission data");
    } finally {
      setIsRefreshing(false);
    }
  };

  const getSubmissionStatusInfo = () => {
    // Get the latest status from API response, fallback to merged status
    const currentStatus = detailed?.status || detailed?.submission?.status || merged?.status;
    
    switch (currentStatus) {
      case "submitted":
        return {
          icon: CheckCircle,
          color: "text-blue-700",
          bg: "bg-blue-50",
          border: "border-blue-200",
          badgeVariant: "default" as const,
        };
      case "reviewed":
        return {
          icon: Eye,
          color: "text-purple-700",
          bg: "bg-purple-50",
          border: "border-purple-200",
          badgeVariant: "secondary" as const,
        };
      case "completed":
        return {
          icon: CheckCircle,
          color: "text-green-700",
          bg: "bg-green-50",
          border: "border-green-200",
          badgeVariant: "default" as const,
        };
      case "returned":
        return {
          icon: AlertCircle,
          color: "text-amber-700",
          bg: "bg-amber-50",
          border: "border-amber-200",
          badgeVariant: "secondary" as const,
        };
      default:
        return {
          icon: AlertCircle,
          color: "text-gray-600",
          bg: "bg-gray-50",
          border: "border-gray-200",
          badgeVariant: "secondary" as const,
        };
    }
  };

  if (isLoading || !merged || isDetailLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-gray-400" />
          <p className="text-gray-500">Loading submission details...</p>
        </div>
      </div>
    );
  }

  if (isDetailError) {
    return (
      <div className="p-6">
        <Alert>
          <AlertDescription>
            Failed to load submission details from the server.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const statusInfo = getSubmissionStatusInfo();

  const getSubmissionHistory = () => {
    if (!merged) return [];
    const submissionDate = new Date(merged.submittedAt);
    const history: Array<any> = [
      {
        id: 1,
        type: "submission",
        title: "Initial Submission",
        description: "Student submitted their work",
        timestamp: submissionDate,
        status: "completed",
        icon: Upload,
        details: {
          files: merged.filesSubmitted?.length ?? 0,
        },
      },
    ];
    if (merged.grade != null) {
      history.push({
        id: 2,
        type: "grade",
        title: "Graded",
        description: `Instructor assigned grade ${merged.grade}/100`,
        timestamp: new Date(submissionDate.getTime() + 24 * 60 * 60 * 1000),
        status: "completed",
        icon: GraduationCap,
        details: { grade: merged.grade, gradedBy: "Instructor" },
      });
    }
    if (merged.feedback) {
      history.push({
        id: 3,
        type: "feedback",
        title: "Feedback Provided",
        description: "Instructor provided feedback",
        timestamp: new Date(submissionDate.getTime() + 25 * 60 * 60 * 1000),
        status: "completed",
        icon: MessageSquare,
        details: { feedback: merged.feedback, providedBy: "Instructor" },
      });
    }
    return history.reverse();
  };

  const handleLaunchSandbox = async () => {
    if (!agreed) {
      toast.error("Please agree to the terms and conditions before launching.");
      return;
    }
    await handleLaunch();
  };

  const handleCancelLaunch = async () => {
    await handleCancel();
  };

  const handleOpenSandbox = (folderPath: string) => {
    if (!user?.name) {
      toast.error("Cannot open sandbox. User not found.");
      return;
    }
    if (sandboxStatus.status !== "active") {
      toast.error("Sandbox is not active. Please launch it first.");
      return;
    }
    setSandboxStatus((prev) => ({ ...prev, lastActivity: new Date().toISOString() }));
    const resolvedFolderPath = (folderPath || workspace.folderPath || `reviews/${submissionId || ""}`)
      .replace(/^\//, "")
      .trim();
    const safeProjectId = encodeURIComponent(resolvedFolderPath);
    navigate(paths.notebook.replace(":projectId", safeProjectId), {
      state: { project: workspace.project ?? { id: submissionId } },
    });
  };

  const handleStopSandbox = async () => {
    await handleStop();
  };


  const handleViewSubmission = (projectId: string, userId: string) => {
    const targetPath = `/projects/${projectId}/submissions/${userId}`;
    navigate(targetPath);
  };

  const handleDownloadAllFiles = async () => {
    try {
      const files = merged?.submission?.filesSubmitted ?? [];
      if (!files.length) {
        toast.error("No files to download");
        return;
      }
      toast.info("Preparing ZIP…");
      const zip = new JSZip();
      for (const f of files) {
        const name =
          f?.fileName ||
          (typeof f?.key === "string"
            ? f.key.split("/").pop() || "file"
            : "file");
        const url: string = f?.presignedUrl?.presignedUrl || f?.url || "";
        if (!url) continue;
        const res = await fetch(url, { method: "GET" });
        if (!res.ok) throw new Error(`Failed to fetch ${name} (${res.status})`);
        const buf = await res.arrayBuffer();
        zip.file(name, buf);
      }
      const student = merged?.studentName?.replace(/\s+/g, "_") || "student";
      const checkpoint =
        merged?.checkpointTitle?.replace(/\s+/g, "_") || "checkpoint";
      const zipName = `${student}-${checkpoint}.zip`;
      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = zipName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("ZIP downloaded");
    } catch (e: any) {
      toast.error(e?.message || "Failed to download ZIP");
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 pt-6 gap-4">
      {/* Top navigation and actions */}
      <div className="flex items-center justify-between p-2">
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-gray-900">Submission Review</h1>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>
      </div>
      <Card className="mx-4 border-0">
        {/* Navigation between filtered submissions */}
        <SubmissionNavigationControls
          onOpenSandbox={undefined}
          showSandboxControls={false}
        />
      </Card>

      <div className="flex-1 overflow-auto px-4 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Overview + Tabs */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="gap-4">
              <CardHeader className="flex items-center justify-between">
                <CardTitle className="flex flex-col">
                  <p className="text-lg font-normal">
                    {merged.submission?.project?.title}
                  </p>
                  <p className="text-base text-muted-foreground">
                    {merged.course}
                  </p>
                </CardTitle>
                <div
                  className={`flex items-center space-x-2 px-3 py-1 rounded-full border ${statusInfo.bg} ${statusInfo.border}`}
                >

                  <p className={`text-sm font-medium ${statusInfo.color}`}>
                    {detailed?.status || detailed?.submission?.status
                      ? (detailed?.status || detailed?.submission?.status).charAt(0).toUpperCase() +
                        (detailed?.status || detailed?.submission?.status).slice(1)
                      : merged.status
                      ? merged.status.charAt(0).toUpperCase() +
                        merged.status.slice(1)
                      : ""}
                  </p>
                </div>
              </CardHeader>

              {/* ── Auto-Grading Status Banner ── */}
              {(() => {
                const submissionStatus =
                  detailed?.status ||
                  (detailed as any)?.submission?.status ||
                  merged.status;
                const autoEval = (detailed as any)?.submission?.metadata?.auto_eval;

                if (submissionStatus === "grading") {
                  return (
                    <div className="mx-6 mb-4 flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <RefreshCw className="h-4 w-4 text-blue-600 animate-spin shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-blue-800">
                          Auto-Grading in Progress
                        </p>
                        <p className="text-xs text-blue-600">
                          The evaluation service is running tests on this
                          notebook. This usually takes 1–3 minutes.
                        </p>
                      </div>
                    </div>
                  );
                }

                if (autoEval && submissionStatus === "graded") {
                  const pct =
                    autoEval.percentage != null
                      ? autoEval.percentage.toFixed(1)
                      : null;
                  const passed = autoEval.test_results?.passed ?? null;
                  const failed = autoEval.test_results?.failed ?? null;
                  const scoreColor =
                    pct >= 80
                      ? "text-green-700"
                      : pct >= 50
                      ? "text-yellow-700"
                      : "text-red-700";
                  const scoreBg =
                    pct >= 80
                      ? "bg-green-50 border-green-200"
                      : pct >= 50
                      ? "bg-yellow-50 border-yellow-200"
                      : "bg-red-50 border-red-200";

                  return (
                    <div
                      className={`mx-6 mb-4 px-4 py-3 border rounded-lg ${scoreBg}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <BadgeCheck
                            className={`h-4 w-4 ${scoreColor}`}
                          />
                          <p
                            className={`text-sm font-semibold ${scoreColor}`}
                          >
                            Auto-Grade Result
                          </p>
                        </div>
                        {pct != null && (
                          <span
                            className={`text-lg font-bold ${scoreColor}`}
                          >
                            {pct}%
                          </span>
                        )}
                      </div>
                      <div className="flex gap-4 text-xs text-gray-600 mb-2">
                        {passed != null && (
                          <span className="flex items-center gap-1 text-green-700">
                            <CheckCircle className="h-3 w-3" />
                            {passed} passed
                          </span>
                        )}
                        {failed != null && (
                          <span className="flex items-center gap-1 text-red-600">
                            <AlertCircle className="h-3 w-3" />
                            {failed} failed
                          </span>
                        )}
                        {autoEval.score != null && (
                          <span className="text-gray-500">
                            {autoEval.score}/{autoEval.max_score} pts
                          </span>
                        )}
                      </div>
                      {(detailed as any)?.submission?.execution_output && (
                        <details className="mt-2">
                          <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                            Show test output
                          </summary>
                          <pre className="mt-2 text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto max-h-48 whitespace-pre-wrap break-all">
                            {(detailed as any).submission.execution_output}
                          </pre>
                        </details>
                      )}
                    </div>
                  );
                }

                return null;
              })()}

              <CardDescription className="px-6 flex">
                <div className="flex-1">
                  <div className="flex text-gray-800">
                    <User className="h-5 w-5 mr-2" />
                    <p>Team Members {merged.group?.name && `( ${merged.group?.name} )`}</p>
                  </div>

                  {/* Check if it's a group submission */}
                  {merged.group ? (

                    <div className="mt-3 ml-4 space-y-3">
                      {merged.group.members.map((member) => {
                        const isSubmitter = member.id === merged.studentId;

                        return (
                          <div key={member.id} className="flex items-center">
                            <div className="flex h-10 w-10 rounded-full bg-bits-blue items-center justify-center flex-shrink-0">
                              <User className="h-5 w-5 text-white" />
                            </div>
                            <div className="ml-2 flex flex-col justify-center flex-1">
                              <div className="flex items-center gap-2">
                                <p className="!text-muted-foreground">
                                  {member.name}
                                </p>
                                {isSubmitter && (
                                  <CheckCircle2 className="h-4 w-4 text-white fill-green-600" />
                                )}
                              </div>
                              <p className="!text-muted-foreground text-sm">
                                {member.email}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    /* Individual submission - single member */
                    <div className="mt-3 ml-4 flex items-center">
                      <div className="flex h-10 w-10 rounded-full bg-bits-blue items-center justify-center">
                        <User className="h-5 w-5 text-white" />
                      </div>
                      <div className="ml-2 flex flex-col justify-center">
                        <p className="!text-muted-foreground">
                          {merged.studentName}
                        </p>
                        <p className="!text-muted-foreground">
                          {merged.studentEmail}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="px-10 flex-1 ">
                  <p className="flex  text-gray-800 mb-2">
                    <Flag className="h-5 w-5 mr-2" /> Checkpoints
                  </p>
                  <span className="h-5 w-5">{merged.checkpointTitle}</span>
                </div>
              </CardDescription>
              <CardContent>
                <div className="grid grid-cols-3 mb-4">
                  <div className="flex-row items-center space-x-2">
                    <p className=" text-gray-800 mb-2 flex">
                      <Calendar className="h-4 w-4 mr-2 text-gray-800" />
                      Submission Date
                    </p>
                    <p className="font-medium text-muted-foreground">
                      {new Date(merged.submittedAt).toLocaleDateString("en-GB")}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex-col">
                      <p className=" text-gray-800 mb-2 flex">
                        <GraduationCap className="h-5 w-5 text-gray-800 mr-2" />
                        Current Grade
                      </p>
                      <p className="text-center">
                        {(() => {
                          // Get the current checkpoint ID from the submission data
                          const currentCheckpointId = detailed?.submission?.checkpoint?.id;
                          
                          if (detailed?.submission?.grade?.rubric_scores && currentCheckpointId) {
                            const checkpointGrade = detailed.submission.grade.rubric_scores.find(
                              (rubricScore: any) => rubricScore.checkpoint_id === currentCheckpointId
                            );
                            
                            if (checkpointGrade) {
                              return `${checkpointGrade.checkpointTotalScore}/${checkpointGrade.checkpointMaxScore}`;
                            }
                          }
                          
                          return detailed?.submission?.grade != null
                            ? `${detailed.submission.grade.total_score}/${detailed.submission.grade.max_score}`
                            : "-";
                        })()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex-col">
                      <p className=" text-gray-800 flex mb-2">
                        <History className="h-5 w-5  text-gray-800 mr-2" />
                        Attempt
                      </p>
                      <p className="text-center text-gray-800">
                        {merged.attempts}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="flex-1 flex flex-col min-h-0">
              <CardHeader>
                <CardTitle>Submission Details</CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col min-h-0">
                <div className="flex-1 flex flex-col min-h-0">
                  <div className="flex-shrink-0">
                    <div className="p-0 -mt-2 -mx-2">
                      <TabNavigation
                        tabs={[
                          {
                            key: "overview",
                            label: "Overview",
                            icon: <Eye className="h-4 w-4" />,
                          },
                          {
                            key: "files",
                            label: "Files",
                            icon: <FileText className="h-4 w-4" />,
                          },
                          {
                            key: "history",
                            label: "Previous Submissions",
                            icon: <History className="h-4 w-4" />,
                          },
                        ]}
                        activeTab={activeTab}
                        onTabChange={(selectedTab) => {
                          if (selectedTab === "history") {
                            handleViewSubmission(merged.assignmentId, user?.id || "");
                          } else {
                            setActiveTab(selectedTab);
                          }
                        }}
                        className="mb-6 mx-2"
                      />
                    </div>
                  </div>

                  <div className="flex-1 overflow-auto min-h-0 mt-6">
                    {activeTab === "overview" && (
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-normal mb-2">Student Comment</h4>
                          <p className="text-gray-600">
                            {merged.comments || "No comments from the student"}
                          </p>
                        </div>
                      </div>
                    )}

                    {activeTab === "files" && (
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium mb-3">Submitted Files</h4>
                          <div className="space-y-2">
                            {merged.submission &&
                            merged.submission.filesSubmitted.length > 0 ? (
                              merged.submission.filesSubmitted.map(
                                (file, index) => (
                                  <div
                                    key={index}
                                    className="flex items-center justify-between p-3 border rounded-lg"
                                  >
                                    <div className="flex items-center space-x-2">
                                      <FileText className="h-4 w-4 text-gray-500" />
                                      <span className="font-medium">
                                        {file.fileName}
                                      </span>
                                    </div>
                                    <div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          const link =
                                            document.createElement("a");
                                          link.href =
                                            file.presignedUrl.presignedUrl;
                                          link.download = file.fileName;
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                        }}
                                      >
                                        <Download className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                )
                              )
                            ) : (
                              <p className="text-gray-500 text-center py-4">
                                No files submitted
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === "history" && (
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium mb-4">
                            Submission Timeline
                          </h4>
                          <div className="relative">
                            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />
                            <div className="space-y-6">
                              {getSubmissionHistory().map((event, index) => {
                                const Icon = event.icon;
                                const isLatest = index === 0;
                                return (
                                  <div
                                    key={event.id}
                                    className="relative flex items-start space-x-4"
                                  >
                                    <div className="relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-2 bg-white border-gray-200">
                                      <Icon className="w-5 h-5 text-gray-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div
                                        className={`p-4 rounded-lg border ${
                                          isLatest
                                            ? "bg-blue-50 border-blue-200"
                                            : "bg-white border-gray-200"
                                        }`}
                                      >
                                        <div className="flex items-start justify-between">
                                          <div className="flex-1">
                                            <h5 className="font-medium text-gray-900">
                                              {event.title}
                                            </h5>
                                            <p className="text-sm text-gray-600 mt-1">
                                              {event.description}
                                            </p>
                                            {event.details?.files != null && (
                                              <p className="mt-2 text-xs text-gray-500">
                                                Files: {event.details.files}
                                              </p>
                                            )}
                                            {event.details?.grade != null && (
                                              <p className="mt-2 text-xs text-gray-500">
                                                Grade: {event.details.grade}/100
                                              </p>
                                            )}
                                            {event.details?.gradedBy && (
                                              <p className="mt-1 text-xs text-gray-500">
                                                Graded by:{" "}
                                                {event.details.gradedBy}
                                              </p>
                                            )}
                                            {event.details?.feedback && (
                                              <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                                                {event.details.feedback.length >
                                                100
                                                  ? event.details.feedback.substring(
                                                      0,
                                                      100
                                                    ) + "..."
                                                  : event.details.feedback}
                                              </div>
                                            )}
                                          </div>
                                          <div className="ml-4 text-right flex-shrink-0">
                                            <p className="text-sm font-medium text-gray-900">
                                              {event.timestamp.toLocaleDateString(
                                                "en-GB"
                                              )}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                              {event.timestamp.toLocaleTimeString(
                                                [],
                                                {
                                                  hour: "2-digit",
                                                  minute: "2-digit",
                                                }
                                              )}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                              {getSubmissionHistory().length === 0 && (
                                <div className="text-center py-8">
                                  <GitBranch className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                  <p className="text-gray-500">
                                    No submission history available.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Quick Stats & Actions */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card className="gap-2">
              <CardHeader>
                <CardTitle className="flex items-center text-xl text-bits-blue font-semibold">
                  <Rocket className="h-6 w-6 mr-2 text-purple-500" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* 1st Step: Setup the local agent tunnel */}
                {workspace.globalSandboxMode === "local" && (
                  <LocalRunButton projectId={submissionId || ""} />
                )}

                {/* Agent Status Badge */}
                {workspace.globalSandboxMode === "local" && workspace.workspaceType === "local" && (
                   <AgentStatusBadge projectId={submissionId || ""} />
                )}

                <SandboxPanel
                  lifecycle={sandboxLifecycle}
                  onOpenSandbox={() => handleOpenSandbox(submissionPath)}
                  projectParams={{ id: submissionId }}
                  agreed={agreed}
                  setAgreed={setAgreed}
                  launchLabel="Launch Review Sandbox"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setIsFeedbackDialogOpen(true)}
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Grades
                </Button>
                <FeedbackDialog
                  isOpen={isFeedbackDialogOpen}
                  onClose={() => setIsFeedbackDialogOpen(false)}
                  submissionId={merged?.submission?.submissionId || submissionId || ""}
                  onSubmit={handleFeedbackSubmit}
                />
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={handleDownloadAllFiles}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Files
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <SandboxTermsModal open={termsOpen} onOpenChange={setTermsOpen} />
    </div>
  );
}
